import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Shell } from "@/components/Layout/Shell";

import Login from "@/pages/Login";
import CambiarContrasena from "@/pages/CambiarContrasena";
import Dashboard from "@/pages/Dashboard";
import Clientes from "@/pages/Clientes";
import Vehiculos from "@/pages/Vehiculos";
import Usuarios from "@/pages/Usuarios";
import Sucursales from "@/pages/Sucursales";
import SinSucursal from "@/pages/SinSucursal";
import FlujosServicio from "@/pages/FlujosServicio";
import MisTrabajos from "@/pages/MisTrabajos";
import MiOrden from "@/pages/MiOrden";
import Configuracion from "@/pages/Configuracion";
import Tenants from "@/pages/Tenants";
import TenantDetail from "@/pages/TenantDetail";
import ServiceOrders from "@/pages/ServiceOrders";
import ServiceOrderNew from "@/pages/ServiceOrderNew";
import ServiceOrderDetail from "@/pages/ServiceOrderDetail";
import PublicPortalOrder from "@/pages/PublicPortalOrder";
import Inventario from "@/pages/Inventario";
import SalesReport from "@/pages/reports/SalesReport";
import CashFlowReport from "@/pages/reports/CashFlow";
import TopCustomers from "@/pages/reports/TopCustomers";
import Kardex from "@/pages/reports/Kardex";
import StockAlerts from "@/pages/reports/StockAlerts";
import Gastos from "@/pages/Gastos";
import GastosCategorias from "@/pages/GastosCategorias";
import Presupuesto from "@/pages/Presupuesto";
import Proveedores from "@/pages/Proveedores";
import Compras from "@/pages/Compras";
import CompraNueva from "@/pages/CompraNueva";
import CompraDetalle from "@/pages/CompraDetalle";
import CuentasPorPagar from "@/pages/CuentasPorPagar";
import CuentaPorPagarDetalle from "@/pages/CuentaPorPagarDetalle";
import Devoluciones from "@/pages/Devoluciones";
import DevolucionDetalle from "@/pages/DevolucionDetalle";
import DocumentosProveedores from "@/pages/DocumentosProveedores";
import DocumentoProveedorDetalle from "@/pages/DocumentoProveedorDetalle";
import PagosProveedores from "@/pages/PagosProveedores";
import HistorialExportaciones from "@/pages/HistorialExportaciones";
import Alertas from "@/pages/Alertas";
import ProductividadTecnicos from "@/pages/ProductividadTecnicos";
import { PermissionGate } from "@/components/PermissionGate";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/cambiar-contrasena"
                element={
                  <ProtectedRoute>
                    <CambiarContrasena />
                  </ProtectedRoute>
                }
              />
              <Route path="/portal/o/:token" element={<PublicPortalOrder />} />
              <Route
                element={
                  <ProtectedRoute>
                    <Shell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/ordenes-de-servicio" element={<ServiceOrders />} />
                <Route path="/ordenes-de-servicio/nueva" element={<ServiceOrderNew />} />
                <Route path="/ordenes-de-servicio/:soId" element={<ServiceOrderDetail />} />
                <Route path="/inventario" element={<Inventario />} />
                <Route path="/inventario/kardex" element={<PermissionGate perm="analytics:view"><Kardex /></PermissionGate>} />
                <Route path="/inventario/alertas" element={<PermissionGate perm="analytics:view"><StockAlerts /></PermissionGate>} />
                <Route path="/reportes/ventas" element={<PermissionGate perm="reports:view"><SalesReport /></PermissionGate>} />
                <Route path="/reportes/flujo" element={<PermissionGate perm="reports:view"><CashFlowReport /></PermissionGate>} />
                <Route path="/reportes/top-clientes" element={<PermissionGate perm="reports:view"><TopCustomers /></PermissionGate>} />
                <Route path="/gastos" element={<PermissionGate perm="expenses:view"><Gastos /></PermissionGate>} />
                <Route path="/gastos/categorias" element={<PermissionGate perm="expenses:edit"><GastosCategorias /></PermissionGate>} />
                <Route path="/gastos/presupuesto" element={<PermissionGate perm="budgets:view"><Presupuesto /></PermissionGate>} />
                <Route path="/proveedores" element={<PermissionGate perm="suppliers:view"><Proveedores /></PermissionGate>} />
                <Route path="/compras" element={<PermissionGate perm="purchase_orders:view"><Compras /></PermissionGate>} />
                <Route path="/compras/nueva" element={<PermissionGate perm="purchase_orders:create"><CompraNueva /></PermissionGate>} />
                <Route path="/compras/:poId/editar" element={<PermissionGate perm="purchase_orders:edit"><CompraNueva /></PermissionGate>} />
                <Route path="/compras/:poId" element={<PermissionGate perm="purchase_orders:view"><CompraDetalle /></PermissionGate>} />
                <Route path="/cuentas-por-pagar" element={<PermissionGate perm="accounts_payable:view"><CuentasPorPagar /></PermissionGate>} />
                <Route path="/cuentas-por-pagar/:apId" element={<PermissionGate perm="accounts_payable:view"><CuentaPorPagarDetalle /></PermissionGate>} />
                <Route path="/devoluciones" element={<PermissionGate perm="purchase_returns:view"><Devoluciones /></PermissionGate>} />
                <Route path="/devoluciones/:prId" element={<PermissionGate perm="purchase_returns:view"><DevolucionDetalle /></PermissionGate>} />
                <Route path="/documentos-proveedores" element={<PermissionGate perm="supplier_documents:view"><DocumentosProveedores /></PermissionGate>} />
                <Route path="/documentos-proveedores/:docId" element={<PermissionGate perm="supplier_documents:view"><DocumentoProveedorDetalle /></PermissionGate>} />
                <Route path="/pagos-proveedores" element={<PermissionGate perm="accounts_payable:view"><PagosProveedores /></PermissionGate>} />
                <Route path="/reportes/historial" element={<PermissionGate perm="report_exports:view"><HistorialExportaciones /></PermissionGate>} />
                <Route path="/alertas" element={<PermissionGate perm="alerts:view"><Alertas /></PermissionGate>} />
                <Route path="/reportes/productividad" element={<PermissionGate perm="analytics:view"><ProductividadTecnicos /></PermissionGate>} />
                <Route path="/clientes" element={<Clientes />} />
                <Route path="/vehiculos" element={<Vehiculos />} />
                <Route path="/usuarios" element={<Usuarios />} />
                <Route path="/sucursales" element={<Sucursales />} />
                <Route path="/sin-sucursal" element={<SinSucursal />} />
                <Route path="/flujos-servicio" element={<FlujosServicio />} />
                <Route path="/mis-trabajos" element={<MisTrabajos />} />
                <Route path="/mis-trabajos/:soId" element={<MiOrden />} />
                <Route path="/configuracion" element={<Configuracion />} />
                <Route
                  path="/tenants"
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <Tenants />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenants/:tenantId"
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <TenantDetail />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
